/**
 * Address autocomplete (spec §14 `suggest`): interactions from the newest cached rows weighted
 * by kind (Sent recipient 5, sender 2, other recipient 1) and decayed by `0.5^(age/60 days)`,
 * plus the contact book; prefix matches ×4, substring ×1, book entries +1.5.
 */
import type { MessageRow } from '$lib/types';

export type InteractionKind = 'sent' | 'from' | 'copy';

export interface Interaction {
  email: string;
  name?: string;
  kind: InteractionKind;
  /** Epoch ms of the message. */
  at: number;
}

export interface Suggestion {
  email: string;
  name: string;
  score: number;
}

export const KIND_WEIGHT: Readonly<Record<InteractionKind, number>> = Object.freeze({ sent: 5, from: 2, copy: 1 });
export const HALF_LIFE_MS = 60 * 24 * 3600e3;
export const PREFIX_BOOST = 4;
export const BOOK_BONUS = 1.5;
/** Rows scanned for interactions. */
export const SUGGEST_SCAN_ROWS = 1200;
export const SUGGEST_LIMIT_COMPOSE = 6;
export const SUGGEST_LIMIT_SEARCH = 3;

const norm = (s: string | null | undefined) => (s || '').trim().toLowerCase();

/** `0.5^(age / 60 days)`; ages ≤ 0 count as fresh. */
export function decay(ageMs: number): number {
  return ageMs <= 0 ? 1 : Math.pow(0.5, ageMs / HALF_LIFE_MS);
}

/** `prefix` when address, local part or any name word starts with the needle; `substr`; `none`. */
export function matchKind(email: string, name: string, needle: string): 'prefix' | 'substr' | 'none' {
  const n = norm(needle);
  if (!n) return 'none';
  const e = norm(email);
  const nm = norm(name);
  const local = e.split('@')[0] || '';
  if (e.startsWith(n) || local.startsWith(n) || nm.split(/[\s@._-]+/).some((w) => w && w.startsWith(n))) return 'prefix';
  if (e.includes(n) || nm.includes(n)) return 'substr';
  return 'none';
}

export interface RankInput {
  needle: string;
  now: number;
  interactions: readonly Interaction[];
  /** email (lower-case) → name. */
  book?: Map<string, string>;
  /** Own address, excluded from results. */
  me?: string;
  limit: number;
}

export function rankSuggestions(input: RankInput): Suggestion[] {
  const me = norm(input.me);
  const book = input.book ?? new Map<string, string>();
  const acc = new Map<string, { name: string; base: number }>();
  const add = (email: string, name: string, weight: number) => {
    const e = norm(email);
    if (!e || !e.includes('@') || e === me) return;
    const cur = acc.get(e) || { name: '', base: 0 };
    cur.base += weight;
    if (!cur.name && name) cur.name = name.trim();
    acc.set(e, cur);
  };
  for (const i of input.interactions) add(i.email, i.name || '', KIND_WEIGHT[i.kind] * decay(input.now - i.at));
  for (const [email, name] of book) add(email, name, 0);
  const out: Suggestion[] = [];
  for (const [email, v] of acc) {
    const name = v.name || book.get(email) || '';
    const kind = matchKind(email, name, input.needle);
    if (kind === 'none') continue;
    const score = (v.base + (book.has(email) ? BOOK_BONUS : 0)) * (kind === 'prefix' ? PREFIX_BOOST : 1);
    out.push({ email, name, score });
  }
  out.sort((a, b) => b.score - a.score || a.email.localeCompare(b.email));
  return out.slice(0, input.limit);
}

/** Turn cached rows into interactions: Sent-folder recipients, senders, other recipients. */
export function buildInteractions(rows: readonly MessageRow[], sentPath: string | null): Interaction[] {
  const out: Interaction[] = [];
  for (const r of rows) {
    const at = Date.parse(r.receivedAt || r.date) || 0;
    if (!at) continue;
    if (sentPath && r.folder === sentPath) {
      for (const to of r.toAddrs ?? []) out.push({ email: to, kind: 'sent', at });
    } else {
      if (r.fromAddr) out.push({ email: r.fromAddr, name: r.fromName, kind: 'from', at });
      for (const to of r.toAddrs ?? []) out.push({ email: to, kind: 'copy', at });
    }
  }
  return out;
}
